import { useEffect, useMemo, useRef, useState } from 'react';
import { bagSubtotal, montagemIndisponivel, repriceBag, type BagItem } from '../../lib/bag';
import { fetchCardapio, fetchSlots, placePedido, type PedidoEndereco, type RuaEntrega, type Slot, type ZonaEntrega } from '../../lib/api';
import {
  buildPedidoPayload,
  contatoValido,
  PAYMENT_LABEL,
  regraDoFrete,
  zonaDoBairro,
  ruaDoEndereco,
  type Payment,
} from '../../lib/order';
import { buildWhatsappHref } from '../../lib/whatsapp';
import { assinaturaDaSacola, chaveDoPedido, liberarChaveDoPedido } from '../../lib/chavePendente';
import { formatBRL } from '../../lib/format';
import { buscarCep, cepDigits, formatCep } from '../../lib/cep';
import { maskCEP, maskPhoneBR, isPhoneBR, maskCNPJ, validarCNPJ } from '../../lib/masks';
import { lerPerfil, salvarPerfil, limparPerfil, enderecoVazio } from '../../lib/clientePerfil';
import { useDialogA11y } from '../../hooks/useDialogA11y';

// Checkout em 4 etapas (Entrega · Contato · Pagamento · Resumo) + confirmação com
// handoff por WhatsApp. O preço NUNCA é enviado — o backend reprecifica. Em falha,
// a sacola é mantida (o pai só limpa após sucesso).

interface Props {
  slug: string;
  shopName: string;
  whatsapp: string | null;
  bag: BagItem[];
  subtotal: number;
  /** Zonas de entrega ATIVAS (Fase 1.1) — para exibir a taxa pelo bairro antes de pagar. */
  zonas: ZonaEntrega[];
  /** Ruas com taxa própria (21/10/2026): valem ANTES da zona do bairro e da taxa padrão. */
  ruas?: RuaEntrega[];
  /** Taxa de entrega PADRÃO da loja (21/10/2026), quando ligada: o frete dos endereços fora das ruas, sem zonas. */
  taxaPadrao?: number | null;
  /** Fuso da LOJA para o agendamento (QA 31/10/2026). Ausente = Brasília. */
  fusoHorario?: string;
  onClose: () => void;
  onOrderPlaced: () => void;
  /** QA-28 A-02 — remoção das linhas que saíram do cardápio/esgotaram, feita PELO PAI (dono da sacola). Sem
   *  isto o cliente lia "revise a sacola" sem nenhuma ação possível dentro do checkout e o Confirmar
   *  devolvia o mesmo 409 para sempre. Opcional: sem o callback, o aviso continua (só sem o botão). */
  onRemoveItens?: (keys: string[]) => void;
}

type Fulfillment = 'ENTREGA' | 'RETIRADA';

const STEPS = ['Entrega', 'Contato', 'Pagamento', 'Resumo'];

// AGENDAMENTO (Fase 2.1) — os próximos 7 dias como opções {iso yyyy-MM-dd, rótulo}. "Hoje"/"Amanhã" + data.
// QA 31/10/2026: no fuso da LOJA, nunca no do celular (um aparelho em UTC mostrava o slot das 19:00 como 22:00 e,
// perto da meia-noite, "Hoje" virava o dia seguinte da loja).
const DIAS_SEMANA_BR = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];
export function proximosDias(qtd: number, fuso = 'America/Sao_Paulo', agora: Date = new Date()): { iso: string; rotulo: string }[] {
  const partes = new Intl.DateTimeFormat('en-CA', { timeZone: fuso, year: 'numeric', month: '2-digit', day: '2-digit' })
    .formatToParts(agora);
  const parte = (t: string): number => Number(partes.find((x) => x.type === t)?.value);
  const out: { iso: string; rotulo: string }[] = [];
  for (let i = 0; i < qtd; i++) {
    // Aritmética de CALENDÁRIO a partir do dia da loja (UTC só como régua, sem fuso).
    const d = new Date(Date.UTC(parte('year'), parte('month') - 1, parte('day') + i));
    const iso = `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${String(d.getUTCDate()).padStart(2, '0')}`;
    const dm = `${String(d.getUTCDate()).padStart(2, '0')}/${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
    const rotulo = i === 0 ? `Hoje ${dm}` : i === 1 ? `Amanhã ${dm}` : `${DIAS_SEMANA_BR[d.getUTCDay()]} ${dm}`;
    out.push({ iso, rotulo });
  }
  return out;
}
/** "HH:mm" no fuso da LOJA a partir do instante ISO do slot. */
export function horaSlot(iso: string, fuso = 'America/Sao_Paulo'): string {
  return new Intl.DateTimeFormat('pt-BR', { timeZone: fuso, hour: '2-digit', minute: '2-digit', hourCycle: 'h23' })
    .format(new Date(iso));
}

interface PlacedSnapshot {
  numero: number;
  nome: string;
  items: BagItem[];
  /** Total dos ITENS (server-priced). */
  total: number;
  /** Taxa de entrega aceita (server-side). */
  taxaEntrega: number;
  /**
   * Entrega com taxa ZERO concedida por uma ZONA (frete grátis), para a confirmação distinguir "Grátis"
   * de "A combinar". Sem isto, taxaEntrega===0 caía sempre em "A combinar", contradizendo o "Grátis" que
   * o cliente viu no resumo/etapa de entrega (QA bloco8 #5).
   */
  entregaGratisPorZona: boolean;
  fulfillment: Fulfillment;
  payment: Payment;
}

/**
 * QA-28 B-04 — os textos de PIX/Cartão citavam o WhatsApp mesmo quando a loja não tem WhatsApp cadastrado
 * (a tela de confirmação já era condicional a `waHref`): o cliente ficava esperando uma mensagem que nunca
 * chegaria. Com WhatsApp, mantém a promessa exata; sem, fala de "atendimento".
 */
function payments(temWhatsapp: boolean): { id: Payment; ico: string; sub: string }[] {
  return [
    {
      id: 'PIX',
      ico: '⚡',
      sub: temWhatsapp
        ? 'Aprovação na hora - QR Code combinado no WhatsApp'
        : 'Aprovação na hora - QR Code combinado no atendimento',
    },
    {
      id: 'CARTAO',
      ico: '💳',
      sub: temWhatsapp ? 'Link seguro enviado no WhatsApp' : 'Link seguro combinado no atendimento',
    },
    { id: 'ENTREGA', ico: '🤝', sub: 'Combine o pagamento ao receber ou retirar' },
  ];
}

/**
 * QA-28 M-14 — traduz um item de `errors[]` do backend ("endereco: tamanho deve ser entre 0 e 500") numa
 * frase em pt-BR + a ETAPA em que o campo mora, para o checkout reconduzir o cliente ao lugar certo em vez
 * de exibir a mensagem crua/genérica.
 */
const CAMPOS_ERRO: { prefixo: string; frase: string; step: number }[] = [
  { prefixo: 'endereco', frase: 'O endereço ficou longo demais - encurte a rua/complemento.', step: 0 },
  { prefixo: 'bairro', frase: 'Confira o bairro informado.', step: 0 },
  { prefixo: 'agendadoPara', frase: 'O horário escolhido não é válido - escolha outro.', step: 0 },
  { prefixo: 'clienteNome', frase: 'Confira o seu nome.', step: 1 },
  { prefixo: 'clienteContato', frase: 'Confira o seu WhatsApp (com DDD).', step: 1 },
  { prefixo: 'documento', frase: 'Confira o CNPJ informado.', step: 1 },
  { prefixo: 'formaPagamento', frase: 'Escolha novamente a forma de pagamento.', step: 2 },
  { prefixo: 'itens', frase: 'Revise os itens da sacola.', step: 3 },
];

export function traduzirErrosValidacao(errors: string[]): { texto: string; step: number | null } {
  const frases: string[] = [];
  let step: number | null = null;
  for (const bruto of errors) {
    const campo = bruto.split(':')[0].trim().toLowerCase();
    const casou = CAMPOS_ERRO.find((c) => campo.startsWith(c.prefixo.toLowerCase()));
    if (casou) {
      if (!frases.includes(casou.frase)) frases.push(casou.frase);
      if (step == null || casou.step < step) step = casou.step;
    } else {
      // Campo desconhecido: mostra o texto do backend em vez de engoli-lo (erro honesto).
      frases.push(bruto.trim());
    }
  }
  return { texto: frases.join(' '), step };
}

export function Checkout({
  slug,
  shopName,
  whatsapp,
  bag,
  subtotal,
  zonas,
  ruas,
  taxaPadrao,
  fusoHorario,
  onClose,
  onOrderPlaced,
  onRemoveItens,
}: Props) {
  // Perfil LOCAL (agilidade SEM login): lido UMA vez na montagem para pré-preencher o checkout, para o
  // público que tem dificuldade em redigitar tudo. Não é autenticação — é conveniência no próprio device.
  const [perfil] = useState(() => lerPerfil(slug));
  const [reconhecido, setReconhecido] = useState<boolean>(perfil != null);
  const [step, setStep] = useState(0);
  const [fulfillment, setFulfillment] = useState<Fulfillment>(perfil?.fulfillment ?? 'ENTREGA');
  const [address, setAddress] = useState<PedidoEndereco & { complemento: string }>(
    () => perfil?.address ?? enderecoVazio()
  );
  const [cepBusy, setCepBusy] = useState(false);
  // QA checkout #4 — feedback quando o CEP não é encontrado (CEP inexistente ou as 2 APIs falharam):
  // sem isto o campo só voltava a ficar "parado", sem dizer ao cliente que precisa preencher na mão.
  const [cepErro, setCepErro] = useState<string | null>(null);
  const [contact, setContact] = useState(() =>
    perfil ? { nome: perfil.nome, phone: perfil.phone } : { nome: '', phone: '' }
  );
  // Cliente EMPRESA (PJ): CNPJ opcional (aceita alfanumérico). Preenchido ⇒ cadastra como pessoa jurídica.
  const [cnpj, setCnpj] = useState('');
  const cnpjInvalido = cnpj.trim().length > 0 && !validarCNPJ(cnpj);
  const [payment, setPayment] = useState<Payment>('PIX');
  const [placing, setPlacing] = useState(false);
  const [erro, setErro] = useState('');
  const [placed, setPlaced] = useState<PlacedSnapshot | null>(null);
  // QA checkout #6 — itens da sacola que a revalidação do catálogo (ao entrar no checkout) achou
  // ESGOTADOS desde que foram adicionados. Bloqueia o envio em vez de só descobrir no 409 do backend.
  // QA-28 A-02 — passa a incluir também o item que SAIU do cardápio (produtoId ausente do catálogo fresco):
  // `repriceBag` preservava essa linha com o preço antigo e ninguém sinalizava, então o backend respondia
  // 409 PRODUTO_INDISPONIVEL e — com a idempotencyKey estável e a sacola inalterada — cada "Confirmar"
  // reproduzia o MESMO erro: loop de "tente novamente" sem saída.
  const [itensIndisponiveis, setItensIndisponiveis] = useState<BagItem[]>([]);
  // Nonce para REVALIDAR a sacola contra o catálogo (após um 409 de produto do backend).
  const [revalidarBump, setRevalidarBump] = useState(0);
  // QA lote4 #6 — sacola REPRECIFICADA contra o catálogo atual (repriceBag): o cliente vê o preço VIGENTE
  // antes de confirmar (o backend reprecifica no envio), e as linhas da confirmação batem com o total
  // server-priced. Inicia = sacola do pai; a revalidação abaixo atualiza após buscar o catálogo fresco.
  const [bagView, setBagView] = useState<BagItem[]>(bag);
  const [precosMudaram, setPrecosMudaram] = useState(false);
  // AGENDAMENTO (Fase 2.1): o cliente escolhe "o quanto antes" (default) ou uma janela. slotSel = ISO início.
  const dias = useMemo(() => proximosDias(7, fusoHorario), [fusoHorario]);
  const [agendar, setAgendar] = useState(false);
  const [slotData, setSlotData] = useState<string>(() => dias[0].iso);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [slotSel, setSlotSel] = useState<string>('');
  // QA-28 M-08 — falha de rede ao buscar os horários é ERRO, não "sem horários neste dia": sem este estado a
  // tela afirmava que a loja não tem agenda e o cliente desistia de agendar por causa de um erro invisível.
  const [slotsErro, setSlotsErro] = useState(false);
  const [slotsCarregando, setSlotsCarregando] = useState(false);
  // Contador para FORÇAR um refetch dos slots (QA bloco13 #4): quando o backend recusa o slot na submissão
  // (SLOT_INDISPONIVEL — o horário lotou na corrida), incrementa para a lista refletir a capacidade atual.
  const [slotsBump, setSlotsBump] = useState(0);
  const closeRef = useRef<HTMLButtonElement | null>(null);
  const dialogRef = useRef<HTMLDivElement | null>(null);
  // QA-28 M-13 — alvo de foco na TROCA de etapa (o foco ficava no rodapé e nada era anunciado).
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const primeiroRender = useRef(true);
  // QA checkout #3 — sequência da última busca de CEP disparada: evita que uma resposta ANTIGA (CEP
  // digitado, corrigido e digitado de novo antes da 1ª resposta voltar) sobrescreva o endereço mais novo.
  const cepSeqRef = useRef(0);
  // Pedido do dono (10/09): quando o CEP carrega o endereço, o cursor tem de PULAR para o número — é o único
  // dado que a consulta de CEP não tem como preencher. Guarda-se também o input do CEP para saber se o
  // cliente AINDA está nele: a busca é assíncrona e mover o foco por baixo de quem já foi digitar outro
  // campo é pior do que não mover.
  const cepInputRef = useRef<HTMLInputElement | null>(null);
  const numeroInputRef = useRef<HTMLInputElement | null>(null);
  // idempotencyKey ESTÁVEL do checkout: reusada em todas as re-tentativas da MESMA sacola, inclusive depois de
  // fechar e reabrir o checkout (guardada na aba, ver lib/chavePendente). Se a 1ª confirmação falha na rede DEPOIS
  // de o backend registrar o pedido, o retry manda a MESMA chave → o backend deduplica em vez de criar um
  // LEAD/pedido duplicado. Só renova após o sucesso ou quando a sacola muda.
  const escopoChave = `loja:${slug}`;

  // AGENDAMENTO (Fase 2.1): busca os slots da data escolhida quando o cliente liga "Agendar". Limpa a
  // seleção ao trocar a data (um slot de outra data não vale). Falha/loja-sem-agenda → lista vazia.
  useEffect(() => {
    if (!agendar) return;
    const ctrl = new AbortController();
    setSlotSel('');
    setSlotsErro(false);
    setSlotsCarregando(true);
    fetchSlots(slug, slotData, ctrl.signal)
      .then((s) => {
        setSlots(s);
        setSlotsCarregando(false);
      })
      .catch((e) => {
        // Abort (troca de data/desmonte) não é erro do cliente — não acende nada.
        if (e instanceof DOMException && e.name === 'AbortError') return;
        // QA-28 M-08 — rede/5xx: diz a verdade ("não conseguimos carregar") + oferece nova tentativa, em vez
        // de mentir "Sem horários neste dia".
        setSlots([]);
        setSlotsErro(true);
        setSlotsCarregando(false);
      });
    return () => ctrl.abort();
  }, [agendar, slotData, slug, slotsBump]);

  // QA-28 M-07 — Tab preso ao diálogo + fundo `aria-hidden` + foco de volta no gatilho ao fechar.
  useDialogA11y(dialogRef);

  useEffect(() => {
    closeRef.current?.focus();
  }, []);

  // QA-28 M-13 — ao trocar de etapa, leva o foco para o corpo da etapa nova (que carrega o `aria-label`
  // "Etapa X de 4: Nome"): sem isto o leitor de tela só anunciava "Continuar pressionado" e o cliente não
  // sabia que já estava em outra etapa. Não roda na montagem (lá o foco é do "×", como sempre).
  useEffect(() => {
    if (primeiroRender.current) {
      primeiroRender.current = false;
      return;
    }
    bodyRef.current?.focus();
  }, [step]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // QA L5 — não fecha por Esc durante o envio (mesma proteção da mesa): fechar desmontaria o Checkout e
      // zeraria a chave de idempotência (useRef por instância) → reabrir reenviaria e DUPLICARIA o LEAD.
      if (e.key === 'Escape' && !placing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, placing]);

  // QA checkout #6 — revalida a sacola contra o catálogo ATUAL ao entrar no checkout: o item pode ter
  // esgotado depois de adicionado (outro cliente esvaziou o estoque, a cozinha zerou o produto). Sem
  // isto, o cliente só descobria no 409 PRODUTO_ESGOTADO do passo final — sem saber QUAL item, e o
  // retry (idempotencyKey estável) reenviava o mesmo pedido inválido em loop. Best-effort: falha de
  // rede aqui não trava o checkout (o 409 do backend continua sendo a rede de segurança final).
  useEffect(() => {
    const ctrl = new AbortController();
    setBagView(bag); // até o catálogo fresco chegar, espelha a sacola do pai (preços de add-time)
    (async () => {
      try {
        const fresh = await fetchCardapio(slug, ctrl.signal);
        // QA lote4 #6 — reprecifica contra o catálogo ATUAL antes de o cliente confirmar.
        const { bag: reprecificada, mudou } = repriceBag(bag, fresh.categorias);
        setBagView(reprecificada);
        setPrecosMudaram(mudou);
        const produtosFrescos = fresh.categorias.flatMap((c) => c.produtos);
        const idsEsgotados = new Set(produtosFrescos.filter((p) => p.esgotado).map((p) => p.produtoId));
        const idsPresentes = new Set(produtosFrescos.map((p) => p.produtoId));
        const frescoPorId = new Map(produtosFrescos.map((p) => [p.produtoId, p]));
        // QA-28 A-02 — item AUSENTE do catálogo fresco também é indisponível (o lojista desativou/removeu o
        // produto). Guarda: um catálogo fresco SEM NENHUM produto não é prova de que os itens saíram (resposta
        // degenerada/gate transitório) — nesse caso não acusa nada e o 409 do backend segue como rede final.
        const podeAcusarAusentes = produtosFrescos.length > 0;
        setItensIndisponiveis((prev) => {
          const next = bag.filter(
            (i) =>
              idsEsgotados.has(i.produto.produtoId) ||
              (podeAcusarAusentes && !idsPresentes.has(i.produto.produtoId)) ||
              // Pizza cuja montagem ficou impossível (sabor, borda ou adicional fora do cardápio).
              (frescoPorId.has(i.produto.produtoId) && montagemIndisponivel(i, frescoPorId.get(i.produto.produtoId)!))
          );
          return next.length === prev.length && next.every((i, idx) => i.key === prev[idx]?.key)
            ? prev
            : next;
        });
      } catch {
        // revalidação é best-effort — não bloqueia o checkout por falha de rede/abort
      }
    })();
    return () => ctrl.abort();
  }, [slug, bag, revalidarBump]);

  // TAXA DE ENTREGA (preview; o backend recalcula na submissão) pela MESMA regra do servidor (21/10/2026): rua com
  // taxa própria → zona do bairro (Fase 1.1) → loja com zonas e bairro fora delas: fora de área → taxa PADRÃO da loja.
  // Antes, loja sem zonas ia "a combinar" e o pedido saía sem a entrega (Don Corderone).
  const usaZonas = zonas.length > 0;
  const bairroInformado = (address.bairro ?? '').trim().length > 0;
  const rua = useMemo(
    () => (fulfillment === 'ENTREGA' ? ruaDoEndereco(ruas ?? [], address.logradouro, address.cidade) : null),
    [fulfillment, address.logradouro, address.cidade, ruas]
  );
  const zona = useMemo(
    () => (fulfillment === 'ENTREGA' && !rua ? zonaDoBairro(zonas, address.bairro) : null),
    [fulfillment, address.bairro, zonas, rua]
  );
  // A taxa padrão só vale sem zonas: quem cadastrou bairros delimitou onde entrega (igual ao servidor).
  const padrao = fulfillment === 'ENTREGA' && !usaZonas && taxaPadrao != null && taxaPadrao > 0 ? taxaPadrao : null;
  // Fora de área: a loja USA zonas, o cliente informou um bairro e ele não casa com nenhuma zona ativa (nem a rua).
  const foraDaArea = fulfillment === 'ENTREGA' && !rua && usaZonas && bairroInformado && zona == null;
  // QA bloco4 #1 — loja COM zonas exige um bairro que case com uma zona: sem bairro o servidor RECUSA (422
  // "informe o bairro"). Bloqueia o envio no preview em paridade com o servidor, em vez de mostrar
  // "A combinar" e deixar o botão ativo (o cliente concluía o checkout todo e só levava o 422 no fim).
  const bairroFaltando = fulfillment === 'ENTREGA' && !rua && usaZonas && !bairroInformado;
  // Pedido do dono (10/09) — endereço SEM número não se entrega. É guarda de UX, não regra nova de servidor:
  // o backend continua aceitando o pedido sem `numero` (campo opcional), mas deixar passar aqui só empurra o
  // problema para o entregador na porta. Mesma forma dos demais gates ("Informe o bairro").
  const numeroFaltando = fulfillment === 'ENTREGA' && (address.numero ?? '').trim().length === 0;
  // QA-28 A-01 — TODA a aritmética de dinheiro do checkout sai do `bagView` (sacola REPRECIFICADA contra o
  // catálogo fresco), não da prop `subtotal` de add-time. Antes as LINHAS do Resumo usavam o preço novo e o
  // Subtotal/Total/pedido-mínimo/frete-grátis usavam o velho: a soma exibida não fechava com as linhas e a
  // tela mentia sobre as regras ("Faltam R$ 6,00 para frete grátis" quando já tinha batido). A prop segue
  // como valor inicial só até o catálogo fresco chegar (bagView começa igual à sacola do pai).
  // `bagView === bag` = catálogo fresco ainda não chegou: usa o subtotal já calculado pelo pai (mesma
  // sacola, mesmo valor); depois da reprecificação, o valor sai SEMPRE do bagView.
  const subtotalView = useMemo(
    () => (bagView === bag ? subtotal : bagSubtotal(bagView)),
    [bag, bagView, subtotal]
  );
  const abaixoDoMinimo = zona != null && zona.pedidoMinimo > 0 && subtotalView < zona.pedidoMinimo;
  // Fase 3.2 — FRETE GRÁTIS: a zona zera a taxa quando o subtotal atinge o limite (preview; o backend
  // recalcula na submissão). Sem limite ou abaixo dele, cobra a taxa da zona.
  const freteGratis =
    zona != null && zona.freteGratisAcima != null && subtotalView >= zona.freteGratisAcima;
  const taxaEntrega = rua ? rua.taxa : zona ? (freteGratis ? 0 : zona.taxa) : (padrao ?? 0);
  const total = subtotalView + taxaEntrega;
  // Bloqueia o envio (com feedback) quando a entrega é impossível pela zona — o backend também barra (422).
  const entregaBloqueada = fulfillment === 'ENTREGA' && (foraDaArea || abaixoDoMinimo || bairroFaltando);
  const freteLabel =
    fulfillment === 'RETIRADA'
      ? 'Grátis'
      : rua
        ? rua.taxa > 0
          ? formatBRL(rua.taxa)
          : 'Grátis'
        : zona
          ? freteGratis
            ? 'Grátis'
            : formatBRL(zona.taxa)
          : foraDaArea
            ? 'Fora da área'
            : bairroFaltando
              ? 'Informe o bairro'
              : padrao != null
                ? formatBRL(padrao)
                : 'A combinar';
  // Quanto falta para o frete grátis (quando a zona oferece e ainda não atingiu) — incentivo no checkout.
  const faltaFreteGratis =
    zona != null && zona.freteGratisAcima != null && !freteGratis
      ? zona.freteGratisAcima - subtotalView
      : null;
  // QA-28 M-04 — o aviso "alguns preços foram atualizados" não dizia O QUE mudou (e só aparecia no Resumo).
  // Lista as linhas alteradas (de → para) comparando a sacola do pai (add-time) com a reprecificada.
  const linhasReprecificadas = useMemo(() => {
    const antes = new Map(bag.map((i) => [i.key, i.unitPrice]));
    return bagView
      .filter((i) => antes.has(i.key) && antes.get(i.key) !== i.unitPrice)
      .map((i) => ({ nome: i.produto.nome, de: antes.get(i.key) as number, para: i.unitPrice }));
  }, [bag, bagView]);
  // QA checkout #5 — mesmo predicado que já ligava aria-invalid, extraído para também condicionar o
  // texto+ícone de erro (feedback não pode depender só de cor).
  const phoneInvalido = contact.phone.length > 0 && !isPhoneBR(contact.phone);

  async function onCep(raw: string) {
    setAddress((a) => ({ ...a, cep: raw }));
    setCepErro(null); // nova digitação: some o erro da busca anterior
    if (cepDigits(raw).length !== 8) {
      // CEP deixou de estar completo (o cliente está corrigindo): invalida a busca em andamento, senão a resposta
      // dela desfazia a correção e preenchia o endereço do CEP errado (QA 31/10/2026).
      cepSeqRef.current++;
      setCepBusy(false);
      return;
    }
    // QA checkout #3 — carimba esta busca com a sequência mais recente; se o cliente corrigir o CEP e
    // disparar uma 2ª busca antes da 1ª responder, só o resultado da busca MAIS NOVA é aplicado.
    const seq = ++cepSeqRef.current;
    setCepBusy(true);
    const r = await buscarCep(raw);
    if (seq !== cepSeqRef.current) return; // resposta antiga: uma busca mais nova já está em curso
    setCepBusy(false);
    if (r) {
      setAddress((a) => ({
        ...a,
        cep: formatCep(r.cep || raw),
        logradouro: r.logradouro || a.logradouro,
        bairro: r.bairro || a.bairro,
        // A cidade PURA (a UF fica no próprio campo): "Cidade · UF" nunca casava a rua cadastrada com cidade (QA 31/10/2026).
        cidade: r.cidade || a.cidade,
        // UF PRESERVADA no estado (antes ela só era concatenada no rótulo da cidade e descartada): vai como
        // campo próprio no pedido para o backend completar o endereço do cadastro.
        uf: r.uf || a.uf,
      }));
      // FOCO AUTOMÁTICO no número (o ponto do pedido do dono): o endereço chegou pronto e o único campo que
      // falta é o número — o cursor já vai para lá em vez de o cliente ter de caçá-lo.
      // A única situação em que NÃO movemos: o cliente já está DIGITANDO em outro campo. A busca é
      // assíncrona; se ela demorou e ele foi preencher a rua/bairro na mão, puxar o cursor por baixo da
      // digitação seria um defeito pior do que o que estamos corrigindo. Foco parado no próprio CEP, em
      // nenhum lugar, ou num botão (o checkout abre com o foco no "Fechar") pode ser movido sem prejuízo.
      const ativo = (typeof document !== 'undefined' ? document.activeElement : null) as HTMLElement | null;
      const digitandoEmOutroCampo =
        ativo != null &&
        ativo !== cepInputRef.current &&
        ['INPUT', 'TEXTAREA', 'SELECT'].includes(ativo.tagName);
      if (!digitandoEmOutroCampo) numeroInputRef.current?.focus();
    } else {
      // QA checkout #4 — CEP inexistente ou as 2 APIs (ViaCEP + BrasilAPI) falharam: avisa em vez de
      // deixar o campo simplesmente "parado" sem explicar por que o endereço não veio.
      setCepErro('Não encontramos esse CEP - preencha o endereço manualmente.');
    }
  }

  function backStep() {
    setErro('');
    if (step === 0) onClose();
    else setStep((s) => s - 1);
  }

  /**
   * QA-28 M-06 — "Continuar" agora VALIDA a etapa antes de avançar. Antes era `setStep(s => s + 1)` puro: o
   * cliente atravessava as 4 etapas com nome/telefone/endereço vazios e só no "Confirmar" era teleportado de
   * volta para a etapa 0/1, sem entender o salto (pior no celular). As regras são as MESMAS do `confirmar()`.
   */
  function avancarStep() {
    if (step === 0) {
      if (fulfillment === 'ENTREGA') {
        const enderecoOk =
          (address.logradouro ?? '').trim().length > 0 &&
          ((address.cidade ?? '').trim().length > 0 || (address.bairro ?? '').trim().length > 0);
        if (!enderecoOk) {
          setErro('Informe o endereço de entrega (rua e cidade/bairro) para continuar.');
          return;
        }
        if (numeroFaltando) {
          setErro('Informe o número do endereço para continuar (use "s/n" se não houver número).');
          return;
        }
        if (foraDaArea) {
          setErro('Ainda não entregamos no seu bairro. Você pode escolher "Retirar no local".');
          return;
        }
        if (bairroFaltando) {
          setErro('Informe o bairro para calcularmos a taxa de entrega.');
          return;
        }
        if (abaixoDoMinimo && zona) {
          setErro(`Pedido mínimo para entrega no seu bairro: ${formatBRL(zona.pedidoMinimo)}.`);
          return;
        }
      }
      if (agendar && !slotSel) {
        setErro('Escolha um horário para agendar, ou selecione "O quanto antes".');
        return;
      }
    }
    if (step === 1) {
      if (!contatoValido(contact.nome, contact.phone)) {
        setErro('Informe seu nome e um WhatsApp válido para continuar.');
        return;
      }
      if (cnpjInvalido) {
        setErro('CNPJ inválido. Confira ou deixe o campo em branco.');
        return;
      }
    }
    setErro('');
    setStep((s) => s + 1);
  }

  /** QA-28 A-02 — remove as linhas indisponíveis pelo pai e revalida. A chave de idempotência é RENOVADA de
   *  propósito: o 409 garante que NADA foi criado no backend, e o pedido que vai agora tem outro conteúdo —
   *  reusar a chave antiga arriscaria uma deduplicação contra um pedido diferente do que o cliente vê. */
  function removerIndisponiveis() {
    if (!onRemoveItens || itensIndisponiveis.length === 0) return;
    onRemoveItens(itensIndisponiveis.map((i) => i.key));
    setItensIndisponiveis([]);
    liberarChaveDoPedido(escopoChave);
    setErro('');
    setRevalidarBump((b) => b + 1);
  }

  async function confirmar() {
    if (!contatoValido(contact.nome, contact.phone)) {
      setErro('Informe seu nome e um WhatsApp válido para enviar o pedido.');
      setStep(1);
      return;
    }
    // CNPJ é OPCIONAL, mas se a empresa preencheu tem de ser válido (evita um 400 no envio).
    if (cnpjInvalido) {
      setErro('CNPJ inválido. Confira ou deixe o campo em branco.');
      setStep(1);
      return;
    }
    // Agendamento (Fase 2.1): escolheu "Agendar" mas não marcou um horário → reconduz ao passo Entrega.
    if (agendar && !slotSel) {
      setErro('Escolha um horário para agendar, ou selecione "O quanto antes".');
      setStep(0);
      return;
    }
    // QA lote11 #3 — ENTREGA exige endereço mínimo ANTES de enviar. Sem este gate o payload seguia sem
    // `endereco`, o backend rejeitava com 400 (ENDERECO_OBRIGATORIO_ENTREGA) e — como a idempotencyKey é
    // estável — cada retry re-disparava o mesmo 400, prendendo o cliente num loop de "tente novamente" sem
    // nunca saber a causa. Reconduz ao passo Entrega (step 0) com mensagem clara (espelha o gate de contato).
    // QA L3 — endereço MÍNIMO real: rua + (cidade ou bairro). flattenEndereco retornava não-vazio com só o
    // CEP ('CEP 17000-000') ou só o complemento, gerando LEAD de ENTREGA sem para onde entregar.
    const enderecoOk =
      (address.logradouro ?? '').trim().length > 0 &&
      ((address.cidade ?? '').trim().length > 0 || (address.bairro ?? '').trim().length > 0);
    if (fulfillment === 'ENTREGA' && !enderecoOk) {
      setErro('Informe o endereço de entrega (rua e cidade/bairro) para enviar o pedido.');
      setStep(0);
      return;
    }
    // Espelha o gate do "Continuar": endereço sem número não se entrega. Reconduz à etapa Entrega, onde o
    // campo mora, em vez de deixar o problema aparecer com o entregador já na rua.
    if (numeroFaltando) {
      setErro('Informe o número do endereço para enviar o pedido (use "s/n" se não houver número).');
      setStep(0);
      return;
    }
    // Fase 1.1 — bloqueios de ZONA (feedback imediato; o backend também barra com 422):
    if (fulfillment === 'ENTREGA' && foraDaArea) {
      setErro('Ainda não entregamos no seu bairro. Você pode escolher "Retirar no local".');
      setStep(0);
      return;
    }
    if (fulfillment === 'ENTREGA' && abaixoDoMinimo && zona) {
      setErro(`Pedido mínimo para entrega no seu bairro: ${formatBRL(zona.pedidoMinimo)}.`);
      setStep(0);
      return;
    }
    // QA checkout #6 — trava aqui como rede de segurança (o botão já vem desabilitado): mesmo que algo
    // burle a UI, não deixa reenviar uma sacola com item esgotado/fora do cardápio (QA-28 A-02).
    if (itensIndisponiveis.length > 0) return;
    if (placing) return;
    setErro('');
    setPlacing(true);
    // Gera a chave só na 1ª tentativa desta sacola; o retry reusa a mesma (evita pedido/LEAD duplicado).
    const idempotencyKey = chaveDoPedido(escopoChave, assinaturaDaSacola(bagView));
    try {
      const payload = buildPedidoPayload({
        bag: bagView,
        clienteNome: contact.nome,
        clienteContato: contact.phone,
        tipoEntrega: fulfillment,
        endereco: fulfillment === 'ENTREGA' ? address : undefined,
        formaPagamento: payment,
        idempotencyKey,
        // Agendamento (Fase 2.1): só quando o cliente escolheu uma janela; senão, imediato.
        agendadoPara: agendar ? slotSel : undefined,
        // Cliente EMPRESA: CNPJ preenchido ⇒ cadastra como PJ (o buildPedidoPayload deriva personType).
        cnpj: cnpj.trim() || undefined,
      });
      const res = await placePedido(slug, payload);
      setPlaced({
        numero: res.numero,
        nome: contact.nome.trim() || 'Cliente',
        items: bagView,
        // QA lote11 #2 / QA checkout #2 — usa o TOTAL AUTORITATIVO devolvido pelo backend (server-priced),
        // não o subtotal calculado no cliente, para a tela "Pedido enviado!" e a mensagem de WhatsApp
        // refletirem o valor realmente registrado (o backend reprecifica cada item pelo catálogo). Sentinel
        // `!= null` (em vez de `> 0`) distingue "backend não devolveu total" (cai no subtotal do front) de
        // "backend devolveu total ZERO legítimo" (pedido 100% cortesia/brinde) — o `> 0` anterior tratava os
        // dois casos como iguais e trocava um total grátis real pelo subtotal do cliente.
        // QA-28 A-01 — fallback = subtotal REPRECIFICADO (bate com as linhas exibidas), não o de add-time.
        total: res.total != null ? res.total : subtotalView,
        // Taxa aceita: o backend recalculou pela zona; cai no preview do cliente se não vier.
        taxaEntrega: res.taxaEntrega != null ? res.taxaEntrega : taxaEntrega,
        // Frete grátis: entrega numa loja que COBRA frete (zona, rua ou taxa padrão) com taxa aceita = 0. Distingue
        // "Grátis" de "A combinar" (loja que não cobra pelo cardápio) na confirmação (QA bloco8 #5). QA 31/10/2026:
        // o cliente com entrega grátis no cadastro, numa loja de taxa padrão, lia "A combinar".
        entregaGratisPorZona:
          fulfillment === 'ENTREGA' &&
          (zona != null || rua != null || (!usaZonas && taxaPadrao != null && taxaPadrao > 0)) &&
          (res.taxaEntrega != null ? res.taxaEntrega : taxaEntrega) === 0,
        fulfillment,
        payment,
      });
      liberarChaveDoPedido(escopoChave); // sucesso → o próximo pedido usa uma chave nova
      onOrderPlaced(); // limpa a sacola no pai (só após sucesso)
      // Agilidade: lembra os dados NO DISPOSITIVO para a próxima visita (sem login). Só após sucesso.
      salvarPerfil(slug, { nome: contact.nome, phone: contact.phone, fulfillment, address });
    } catch (e) {
      // QA lote11 #3 — surge a CAUSA real do backend (placePedido agora lê body.detail/message) em vez de
      // uma mensagem sempre genérica; fallback amigável quando é falha de rede sem detalhe.
      const detalhe = e instanceof Error ? e.message.trim() : '';
      // QA bloco13 #4 — slot recusado na submissão (lotou/expirou na corrida): reconduz ao passo Entrega,
      // limpa a seleção agora inválida e REVALIDA a lista (refetch), em vez de deixar o cliente preso no
      // Resumo reconfirmando o MESMO slot stale num loop. Espelha os demais gates que fazem setStep(0).
      // Check ESTRUTURAL pelo `code` (não instanceof CardapioError) — robusto quando o módulo de api é mockado.
      const code = (e as { code?: unknown } | null)?.code;
      if (code === 'SLOT_INDISPONIVEL') {
        setSlotSel('');
        setSlotsBump((b) => b + 1);
        setStep(0);
      }
      // QA-28 A-02 — 409 de PRODUTO (saiu do cardápio / indisponível hoje / esgotou na corrida): revalida a
      // sacola contra o catálogo AGORA, para o aviso nomear o item e liberar o botão "Remover ... e
      // continuar". Sem isto o cliente reconfirmava o MESMO pedido inválido para sempre.
      const CODES_PRODUTO = ['PRODUTO_INDISPONIVEL', 'PRODUTO_INDISPONIVEL_HOJE', 'PRODUTO_ESGOTADO'];
      if (typeof code === 'string' && CODES_PRODUTO.includes(code)) {
        setRevalidarBump((b) => b + 1);
        setErro(
          (detalhe ? detalhe + ' ' : '') +
            'Revise a sacola: vamos marcar o item indisponível para você removê-lo.'
        );
        return;
      }
      // QA-28 M-14 — 422 de validação: o `detail` do backend é fixo/genérico; os detalhes vêm em `errors[]`.
      // Traduz para pt-BR por campo e reconduz à etapa onde o campo mora.
      const errors = (e as { errors?: unknown } | null)?.errors;
      if (Array.isArray(errors) && errors.length > 0) {
        const { texto, step: destino } = traduzirErrosValidacao(errors.filter((x): x is string => typeof x === 'string'));
        if (texto) {
          setErro(texto);
          if (destino != null) setStep(destino);
          return;
        }
      }
      setErro(detalhe || 'Não foi possível enviar o pedido agora. Tente novamente em instantes.');
    } finally {
      setPlacing(false);
    }
  }

  // "Não é você?": apaga o perfil local e zera os campos pré-preenchidos (ex.: device compartilhado).
  function esquecerPerfil() {
    limparPerfil(slug);
    setReconhecido(false);
    setContact({ nome: '', phone: '' });
    setCnpj(''); // device compartilhado: não deixa o CNPJ da empresa anterior no campo
    setAddress(enderecoVazio());
  }

  const waHref =
    placed &&
    buildWhatsappHref(whatsapp, {
      shopName,
      numero: placed.numero,
      itens: placed.items.map((i) => ({
        qty: i.qty,
        nome: i.produto.nome,
        variacao: i.pizzaResumo?.join(' · ') ?? i.variacao?.nome,
        adicionais: i.adicionais.map((a) => a.nome),
      })),
      entregaLabel: placed.fulfillment === 'RETIRADA' ? 'Retirada no local' : 'Entrega',
      pagamentoLabel: PAYMENT_LABEL[placed.payment],
      total: placed.total + placed.taxaEntrega,
    });

  return (
    <div
      className="loja-overlay loja-overlay--center"
      onClick={(e) => e.target === e.currentTarget && !placing && onClose()}
    >
      <div
        ref={dialogRef}
        className="loja-modal loja-checkout"
        role="dialog"
        aria-modal="true"
        aria-label="Finalizar pedido"
      >
        <button
          ref={closeRef}
          className="loja-close loja-close--float"
          onClick={onClose}
          disabled={placing}
          aria-label="Fechar"
        >
          ×
        </button>

        {!placed && reconhecido && (
          <div className="loja-welcome-back">
            <div className="loja-welcome-back__txt">
              <strong>
                👋 Que bom te ver de novo
                {contact.nome.trim() ? `, ${contact.nome.trim().split(' ')[0]}` : ''}!
              </strong>
              <span>Já deixamos seus dados prontos. Confira e é só enviar.</span>
            </div>
            <button
              type="button"
              className="loja-welcome-back__reset"
              onClick={esquecerPerfil}
            >
              Não é você?
            </button>
          </div>
        )}

        {placed ? (
          <div className="loja-confirm">
            <div className="loja-confirm-badge" aria-hidden="true">
              ✓
            </div>
            <h2>Pedido enviado!</h2>
            {/* QA L3 — só instrui "Falar no WhatsApp" quando a loja TEM WhatsApp (o botão é condicional a
                waHref); sem ele, a loja retorna pelo telefone informado — sem citar um botão inexistente. */}
            <p className="loja-confirm-lead">
              Obrigado, {placed.nome.split(' ')[0]}! Seu pedido <b>#{placed.numero}</b> chegou à {shopName}.{' '}
              {waHref
                ? 'Toque em Falar no WhatsApp para combinar o pagamento e a entrega.'
                : 'A loja vai te chamar no WhatsApp/telefone informado para combinar o pagamento e a entrega.'}
            </p>
            <div className="loja-confirm-box">
              {placed.items.map((i) => (
                <div className="loja-confirm-row" key={i.key}>
                  <span>
                    {i.qty}× {i.produto.nome}
                    {i.variacao ? ` · ${i.variacao.nome}` : ''}
                    {/* QA L2 — recap dos adicionais (paridade com a etapa Resumo e o WhatsApp). */}
                    {i.pizzaResumo?.length ? ` · ${i.pizzaResumo.join(' · ')}` : i.adicionais.length > 0 ? ` · ${i.adicionais.map((a) => a.nome).join(', ')}` : ''}
                  </span>
                  <b>{formatBRL(i.unitPrice * i.qty)}</b>
                </div>
              ))}
              <div className="loja-confirm-row loja-confirm-row--sub">
                <span>{placed.fulfillment === 'RETIRADA' ? 'Retirada no local' : 'Entrega'}</span>
                <b>
                  {placed.fulfillment === 'RETIRADA'
                    ? 'Grátis'
                    : placed.taxaEntrega > 0
                      ? formatBRL(placed.taxaEntrega)
                      : placed.entregaGratisPorZona
                        ? 'Grátis'
                        : 'A combinar'}
                </b>
              </div>
              <div className="loja-confirm-row loja-confirm-row--total">
                <span>Total · {PAYMENT_LABEL[placed.payment]}</span>
                <b>{formatBRL(placed.total + placed.taxaEntrega)}</b>
              </div>
            </div>
            {waHref && (
              <a
                className="loja-btn loja-btn--wa loja-btn--lg loja-btn--block"
                href={waHref}
                target="_blank"
                rel="noreferrer"
              >
                <span aria-hidden="true">💬</span> Falar no WhatsApp
              </a>
            )}
            <button className="loja-btn loja-btn--text loja-btn--block" onClick={onClose}>
              Voltar ao cardápio
            </button>
          </div>
        ) : (
          <>
            <div className="loja-checkout-head">
              <h2>Finalizar pedido</h2>
              {/* QA-28 M-13 — passos com semântica: lista + `aria-current="step"` no ativo e rótulo
                  "Etapa X de N: Nome" (antes eram <div>s só com classes is-on/is-done — invisíveis para
                  leitor de tela). */}
              <div className="loja-steps" role="list">
                {STEPS.map((s, idx) => (
                  <div
                    key={s}
                    role="listitem"
                    aria-current={idx === step ? 'step' : undefined}
                    aria-label={`Etapa ${idx + 1} de ${STEPS.length}: ${s}`}
                    className={`loja-step${idx === step ? ' is-on' : ''}${idx < step ? ' is-done' : ''}`}
                  >
                    <i>{idx < step ? '✓' : idx + 1}</i>
                    <span>{s}</span>
                  </div>
                ))}
              </div>
              <div className="loja-steps-track" aria-hidden="true">
                <i style={{ width: `${(step / (STEPS.length - 1)) * 100}%` }} />
              </div>
            </div>

            <div
              className="loja-checkout-body"
              ref={bodyRef}
              tabIndex={-1}
              aria-label={`Etapa ${step + 1} de ${STEPS.length}: ${STEPS[step]}`}
            >
              {/* QA-28 B-02 — alerta com ÍCONE + texto (nunca status só por cor), igual ao .loja-field-error. */}
              {erro && (
                <p className="loja-alert" role="alert">
                  <span aria-hidden="true">⚠</span> {erro}
                </p>
              )}

              {/* QA checkout #6 / QA-28 A-02 — aviso persistente (qualquer etapa) enquanto a sacola tiver
                  item esgotado OU fora do cardápio, com a AÇÃO de remover ali mesmo: antes mandava "feche o
                  checkout e remova", e no caso do item removido do cardápio não avisava nada — o cliente só
                  levava um 409 genérico, em loop. */}
              {itensIndisponiveis.length > 0 && (
                <div className="loja-alert" role="alert">
                  <span aria-hidden="true">⚠</span>{' '}
                  {itensIndisponiveis.length === 1
                    ? 'Este item não está mais disponível'
                    : 'Estes itens não estão mais disponíveis'}
                  : <b>{itensIndisponiveis.map((i) => i.produto.nome).join(', ')}</b>.
                  {onRemoveItens ? (
                    <>
                      {' '}
                      <button type="button" className="loja-btn loja-btn--text" onClick={removerIndisponiveis}>
                        {itensIndisponiveis.length === 1
                          ? 'Remover item indisponível e continuar'
                          : 'Remover itens indisponíveis e continuar'}
                      </button>
                    </>
                  ) : (
                    <>
                      {' '}
                      Feche o checkout e remova-o{itensIndisponiveis.length === 1 ? '' : 's'} da sacola para
                      continuar.
                    </>
                  )}
                </div>
              )}

              {/* QA-28 M-04 — aviso de preço atualizado em TODA etapa (não só no Resumo), com faixa de
                  atenção, ícone e o de → para de cada linha: antes era um <p> de classe inexistente, sem
                  role, na última etapa — a chance de o cliente notar era quase nula. */}
              {precosMudaram && (
                <div className="loja-review-aviso" role="status">
                  <span aria-hidden="true">ℹ</span> Alguns preços foram atualizados desde que você montou a
                  sacola.
                  {linhasReprecificadas.length > 0 && (
                    <ul className="loja-review-aviso-lista">
                      {linhasReprecificadas.map((l) => (
                        <li key={l.nome + l.de}>
                          {l.nome}: {formatBRL(l.de)} → <b>{formatBRL(l.para)}</b>
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              )}

              {/* Etapa 1 — Entrega */}
              {step === 0 && (
                <div className="loja-fields">
                  <div className="loja-opts">
                    <button
                      className={`loja-opt-card${fulfillment === 'ENTREGA' ? ' is-on' : ''}`}
                      aria-pressed={fulfillment === 'ENTREGA'}
                      onClick={() => setFulfillment('ENTREGA')}
                    >
                      <span className="loja-opt-ico" aria-hidden="true">
                        🛵
                      </span>
                      <b>Receber em casa</b>
                      <small>{regraDoFrete(zonas, ruas, taxaPadrao) ?? 'Frete a combinar'}</small>
                    </button>
                    <button
                      className={`loja-opt-card${fulfillment === 'RETIRADA' ? ' is-on' : ''}`}
                      aria-pressed={fulfillment === 'RETIRADA'}
                      onClick={() => setFulfillment('RETIRADA')}
                    >
                      <span className="loja-opt-ico" aria-hidden="true">
                        🏪
                      </span>
                      <b>Retirar no local</b>
                      <small>Grátis</small>
                    </button>
                  </div>
                  {fulfillment === 'ENTREGA' ? (
                    <div className="loja-form">
                      <label className="loja-field">
                        <span>CEP{cepBusy ? ' · buscando…' : ''}</span>
                        <input
                          ref={cepInputRef}
                          value={address.cep}
                          onChange={(e) => onCep(maskCEP(e.target.value))}
                          placeholder="00000-000"
                          inputMode="numeric"
                          autoComplete="postal-code"
                          aria-invalid={cepErro != null}
                        />
                        {cepErro && (
                          <span className="loja-field-error" role="alert">
                            <span aria-hidden="true">⚠</span> {cepErro}
                          </span>
                        )}
                      </label>
                      {/* Pedido do dono (10/09) — rua e NÚMERO em campos SEPARADOS, lado a lado (padrão de
                          todo checkout de delivery). Antes o rótulo pedia "Endereço e número" num campo só: o
                          número ficava perdido dentro do texto da rua, e o backend não conseguia usá-lo como
                          identidade do local (CEP+número) para reconhecer o cliente no pedido seguinte. */}
                      <div className="loja-form-linha">
                        <label className="loja-field">
                          <span>Endereço</span>
                          <input
                            value={address.logradouro}
                            onChange={(e) => setAddress({ ...address, logradouro: e.target.value })}
                            placeholder="Rua das Flores"
                            autoComplete="address-line1"
                            // F2-B2 — logradouro+complemento alimentam o `endereco` achatado (VARCHAR(500) no
                            // backend, @Size(max=500) em CriarPedidoCardapioRequest.endereco); sem o maxLength
                            // o cliente só descobria o limite no 400 do envio.
                            maxLength={500}
                          />
                        </label>
                        <label className="loja-field">
                          <span>Número</span>
                          <input
                            ref={numeroInputRef}
                            value={address.numero ?? ''}
                            onChange={(e) => setAddress({ ...address, numero: e.target.value })}
                            placeholder="123"
                            autoComplete="address-line2"
                            // SEM inputMode="numeric" de propósito: no iOS esse teclado não oferece letras e
                            // prenderia quem mora em "123-A", "s/n" ou "Km 4" — endereços reais. O conforto de
                            // um teclado numérico não paga um endereço impossível de digitar.
                            // Paridade com o backend: @Size(max=20) em CriarPedidoCardapioRequest.numero.
                            maxLength={20}
                          />
                        </label>
                      </div>
                      <label className="loja-field">
                        <span>Bairro</span>
                        <input
                          value={address.bairro}
                          onChange={(e) => setAddress({ ...address, bairro: e.target.value })}
                          placeholder="Centro"
                          autoComplete="address-level3"
                          // F2-B2 — bairro é enviado como campo ESTRUTURADO próprio (@Size(max=80)).
                          maxLength={80}
                        />
                      </label>
                      <label className="loja-field">
                        <span>Cidade</span>
                        <input
                          value={address.cidade}
                          onChange={(e) => setAddress({ ...address, cidade: e.target.value })}
                          autoComplete="address-level2"
                          // F2-B2 — cidade tem o mesmo limite de bairro no backend (@Size(max=80)).
                          maxLength={80}
                        />
                      </label>
                      <label className="loja-field loja-field--full">
                        <span>Complemento / referência</span>
                        <input
                          value={address.complemento}
                          onChange={(e) => setAddress({ ...address, complemento: e.target.value })}
                          placeholder="Apto, ponto de referência…"
                          autoComplete="address-line3"
                          // F2-B2 — complemento também alimenta o `endereco` achatado (VARCHAR(500)).
                          maxLength={500}
                        />
                      </label>
                    </div>
                  ) : (
                    <div className="loja-pickup">
                      <b>Retirada no local</b>
                      <p>Avisaremos no WhatsApp assim que o seu pedido estiver pronto.</p>
                    </div>
                  )}
                  {/* Fase 1.1 — taxa de entrega pelo BAIRRO, exibida ANTES de pagar (fora de área / mínimo). */}
                  {fulfillment === 'ENTREGA' && foraDaArea && (
                    <p className="loja-alert" role="alert">
                      <span aria-hidden="true">⚠</span> Ainda não entregamos no bairro{' '}
                      <b>{address.bairro}</b>. Você pode escolher <b>Retirar no local</b>.
                    </p>
                  )}
                  {/* 21/10/2026 — a rua com taxa própria e a taxa padrão da loja, exibidas ANTES de pagar. */}
                  {fulfillment === 'ENTREGA' && rua && (
                    <p className="loja-hint">
                      {rua.taxa > 0 ? (
                        <>
                          Taxa de entrega para <b>{rua.logradouro}</b>: <b>{formatBRL(rua.taxa)}</b>.
                        </>
                      ) : (
                        <>
                          🎉 <b>Entrega grátis</b> para <b>{rua.logradouro}</b>.
                        </>
                      )}
                    </p>
                  )}
                  {fulfillment === 'ENTREGA' && !rua && padrao != null && (address.logradouro ?? '').trim() !== '' && (
                    <p className="loja-hint">
                      Taxa de entrega: <b>{formatBRL(padrao)}</b>.
                    </p>
                  )}
                  {fulfillment === 'ENTREGA' && zona && (
                    <p className="loja-hint">
                      Taxa de entrega para <b>{zona.bairro}</b>:{' '}
                      <b>{freteGratis ? 'Grátis' : formatBRL(zona.taxa)}</b>
                      {zona.prazoEstimadoMin ? ` · aprox. ${zona.prazoEstimadoMin} min` : ''}.
                      {freteGratis && (
                        <>
                          {' '}
                          🎉 Você ganhou <b>frete grátis</b>!
                        </>
                      )}
                      {faltaFreteGratis != null && faltaFreteGratis > 0 && (
                        <>
                          {' '}
                          Faltam <b>{formatBRL(faltaFreteGratis)}</b> para o frete grátis.
                        </>
                      )}
                      {abaixoDoMinimo && (
                        <>
                          {' '}
                          Pedido mínimo {formatBRL(zona.pedidoMinimo)} - faltam{' '}
                          {/* QA-28 A-01 — falta calculada sobre o subtotal REPRECIFICADO (o mesmo que decide
                              `abaixoDoMinimo` e que o backend recalcula). */}
                          {formatBRL(zona.pedidoMinimo - subtotalView)} para entregar aqui.
                        </>
                      )}
                    </p>
                  )}

                  {/* AGENDAMENTO (Fase 2.1): "o quanto antes" ou agendar uma janela com capacidade. */}
                  <div className="loja-quando">
                    <div className="loja-quando-h">Quando?</div>
                    <div className="loja-opts">
                      <button
                        type="button"
                        className={`loja-opt-card${!agendar ? ' is-on' : ''}`}
                        aria-pressed={!agendar}
                        onClick={() => setAgendar(false)}
                      >
                        <span className="loja-opt-ico" aria-hidden="true">
                          ⚡
                        </span>
                        <b>O quanto antes</b>
                        <small>Preparamos assim que possível</small>
                      </button>
                      <button
                        type="button"
                        className={`loja-opt-card${agendar ? ' is-on' : ''}`}
                        aria-pressed={agendar}
                        onClick={() => setAgendar(true)}
                      >
                        <span className="loja-opt-ico" aria-hidden="true">
                          📅
                        </span>
                        <b>Agendar</b>
                        <small>Escolha dia e horário</small>
                      </button>
                    </div>
                    {agendar && (
                      <div className="loja-agendar">
                        <label className="loja-field">
                          <span>Dia</span>
                          <select value={slotData} onChange={(e) => setSlotData(e.target.value)}>
                            {dias.map((d) => (
                              <option key={d.iso} value={d.iso}>
                                {d.rotulo}
                              </option>
                            ))}
                          </select>
                        </label>
                        {/* QA-28 M-08 — três estados HONESTOS: carregando, falha (com nova tentativa) e
                            realmente sem horários. Antes qualquer falha de rede virava "Sem horários neste
                            dia" e o cliente desistia de agendar por causa de um erro invisível. */}
                        {slotsErro ? (
                          <p className="loja-alert" role="alert">
                            <span aria-hidden="true">⚠</span> Não conseguimos carregar os horários.{' '}
                            <button
                              type="button"
                              className="loja-btn loja-btn--text"
                              onClick={() => setSlotsBump((b) => b + 1)}
                            >
                              Tentar de novo
                            </button>
                          </p>
                        ) : slotsCarregando ? (
                          <p className="loja-hint" role="status">
                            Carregando horários…
                          </p>
                        ) : slots.length === 0 ? (
                          <p className="loja-hint">
                            Sem horários neste dia. Escolha outro dia ou <b>O quanto antes</b>.
                          </p>
                        ) : (
                          <div className="loja-slots" role="group" aria-label="Horários disponíveis">
                            {slots.map((s) => (
                              <button
                                key={s.inicio}
                                type="button"
                                className={`loja-slot${slotSel === s.inicio ? ' is-on' : ''}`}
                                disabled={!s.disponivel}
                                aria-pressed={slotSel === s.inicio}
                                onClick={() => setSlotSel(s.inicio)}
                              >
                                {horaSlot(s.inicio, fusoHorario)}
                                {!s.disponivel
                                  ? ' · lotado'
                                  : s.restantes <= 3
                                    ? ` · ${s.restantes} vaga${s.restantes === 1 ? '' : 's'}`
                                    : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Etapa 2 — Contato */}
              {step === 1 && (
                <div className="loja-form">
                  <label className="loja-field loja-field--full">
                    <span>Seu nome</span>
                    <input
                      value={contact.nome}
                      onChange={(e) => setContact({ ...contact, nome: e.target.value })}
                      placeholder="Como podemos te chamar?"
                      maxLength={200}
                    />
                  </label>
                  <label className="loja-field loja-field--full">
                    <span>WhatsApp</span>
                    <input
                      value={contact.phone}
                      onChange={(e) => setContact({ ...contact, phone: maskPhoneBR(e.target.value) })}
                      placeholder="(14) 99999-0000"
                      inputMode="tel"
                      aria-invalid={phoneInvalido}
                    />
                    {/* QA checkout #5 — feedback do telefone inválido não pode depender só de cor
                        (aria-invalid + borda): texto + ícone servem quem não distingue a mudança de cor. */}
                    {phoneInvalido && (
                      <span className="loja-field-error" role="alert">
                        <span aria-hidden="true">⚠</span> Informe um WhatsApp válido com DDD
                      </span>
                    )}
                  </label>
                  {/* Cliente EMPRESA: CNPJ opcional (aceita alfanumérico). Preenchido ⇒ cadastra como PJ. */}
                  <label className="loja-field loja-field--full">
                    <span>CNPJ da empresa (opcional)</span>
                    <input
                      value={cnpj}
                      onChange={(e) => setCnpj(maskCNPJ(e.target.value))}
                      placeholder="00.000.000/0000-00"
                      aria-invalid={cnpjInvalido}
                      aria-describedby={cnpjInvalido ? 'chk-cnpj-err' : undefined}
                    />
                    {cnpjInvalido && (
                      <span id="chk-cnpj-err" className="loja-field-error" role="alert">
                        <span aria-hidden="true">⚠</span> CNPJ inválido
                      </span>
                    )}
                  </label>
                  <p className="loja-hint">
                    Usamos seu WhatsApp só para confirmar e combinar a entrega deste pedido. Empresa? Informe o
                    CNPJ para o pedido sair no nome da sua empresa.
                  </p>
                </div>
              )}

              {/* Etapa 3 — Pagamento */}
              {step === 2 && (
                <div className="loja-fields">
                  {/* QA-28 B-04 — os subtítulos só citam WhatsApp quando a loja TEM WhatsApp. */}
                  {payments(!!whatsapp && whatsapp.trim().length > 0).map((m) => (
                    <button
                      key={m.id}
                      className={`loja-pay${payment === m.id ? ' is-on' : ''}`}
                      aria-pressed={payment === m.id}
                      onClick={() => setPayment(m.id)}
                    >
                      <span className="loja-pay-ico" aria-hidden="true">
                        {m.ico}
                      </span>
                      <div>
                        <b>{PAYMENT_LABEL[m.id]}</b>
                        <small>{m.sub}</small>
                      </div>
                      <span className="loja-pay-radio" aria-hidden="true" />
                    </button>
                  ))}
                </div>
              )}

              {/* Etapa 4 — Resumo */}
              {step === 3 && (
                <div className="loja-review">
                  {/* QA-28 M-04 — o aviso de preço atualizado subiu para o topo do corpo (todas as etapas). */}
                  {/* QA-28 M-05 — o "Confirmar pedido" nasce desabilitado quando a entrega está bloqueada
                      pela zona, e o motivo só aparecia na etapa 0: aqui o cliente lê o motivo E o atalho. */}
                  {entregaBloqueada && (
                    <p className="loja-alert" role="alert" id="chk-entrega-bloqueada">
                      <span aria-hidden="true">⚠</span>{' '}
                      {foraDaArea
                        ? 'Ainda não entregamos no seu bairro - volte à etapa Entrega e escolha "Retirar no local".'
                        : bairroFaltando
                          ? 'Informe o bairro na etapa Entrega para calcularmos a taxa.'
                          : zona
                            ? `Pedido mínimo para entrega no seu bairro: ${formatBRL(zona.pedidoMinimo)} - faltam ${formatBRL(Math.max(0, zona.pedidoMinimo - subtotalView))}.`
                            : 'Não é possível entregar neste endereço - volte à etapa Entrega.'}
                    </p>
                  )}
                  <div className="loja-review-items">
                    {bagView.map((i) => (
                      <div className="loja-confirm-row" key={i.key}>
                        <span>
                          {i.qty}× {i.produto.nome}
                          {i.variacao ? ` · ${i.variacao.nome}` : ''}
                          {i.pizzaResumo?.length
                            ? ` · ${i.pizzaResumo.join(' · ')}`
                            : i.adicionais.length > 0 ? ` · ${i.adicionais.map((a) => a.nome).join(', ')}` : ''}
                        </span>
                        <b>{formatBRL(i.unitPrice * i.qty)}</b>
                      </div>
                    ))}
                  </div>
                  <div className="loja-review-meta">
                    <div>
                      <span>Entrega</span>
                      <b>
                        {fulfillment === 'RETIRADA'
                          ? 'Retirada no local'
                          : `${address.logradouro || 'Endereço'}${address.numero ? `, ${address.numero}` : ''}${address.cidade ? ` · ${address.cidade}` : ''}`}
                      </b>
                    </div>
                    <div>
                      <span>Contato</span>
                      <b>
                        {contact.nome || 'Cliente'} · {contact.phone || 'WhatsApp'}
                      </b>
                    </div>
                    <div>
                      <span>Pagamento</span>
                      <b>{PAYMENT_LABEL[payment]}</b>
                    </div>
                  </div>
                  <div className="loja-review-totals">
                    <div className="loja-sumline">
                      <span>Subtotal</span>
                      {/* QA-28 A-01 — MESMA base das linhas acima (bagView reprecificado): antes as linhas
                          somavam R$ 96 e o Subtotal dizia R$ 90. */}
                      <b>{formatBRL(subtotalView)}</b>
                    </div>
                    <div className="loja-sumline">
                      <span>Frete</span>
                      <b className={fulfillment === 'RETIRADA' ? 'loja-free' : ''}>{freteLabel}</b>
                    </div>
                    <div className="loja-sumline loja-sumline--grand">
                      <span>Total</span>
                      <b>{formatBRL(total)}</b>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="loja-checkout-foot">
              <div className="loja-checkout-total">
                <span>Total</span>
                <b>{formatBRL(total)}</b>
              </div>
              <div className="loja-checkout-nav">
                <button className="loja-btn loja-btn--ghost" onClick={backStep}>
                  {step === 0 ? 'Voltar à sacola' : 'Voltar'}
                </button>
                {step < STEPS.length - 1 ? (
                  <button
                    className="loja-btn loja-btn--primary loja-btn--lg"
                    // QA-28 M-06 — valida a etapa ANTES de avançar (erro na própria etapa).
                    onClick={avancarStep}
                  >
                    Continuar
                  </button>
                ) : (
                  <button
                    className="loja-btn loja-btn--primary loja-btn--lg"
                    onClick={() => void confirmar()}
                    disabled={placing || itensIndisponiveis.length > 0 || entregaBloqueada}
                    // QA-28 M-05 — o botão desabilitado passa a APONTAR o motivo (alerta acima), em vez de
                    // apagar sem explicação no fim do fluxo.
                    aria-describedby={entregaBloqueada ? 'chk-entrega-bloqueada' : undefined}
                  >
                    {placing ? 'Enviando…' : 'Confirmar pedido'}
                  </button>
                )}
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
